<template>
  <div style="width: 100%">
    <q-input
      ref="addressInput"
      v-model="searchText"
      :autofocus="autofocus"
      :dense="dense"
      :disable="disable"
      :loading="loading"
      :placeholder="placeholder"
      clearable
      filled
      flat
      @focus="openMenu = true"
      @keydown.down.prevent="focusNext(0)"
      @keydown.tab.prevent="focusNext(0)"
    >
      <template v-if="showIcon" #prepend>
        <q-icon name="search" />
      </template>
    </q-input>
    <q-menu
      id="intercom-address-autocomplete-menu"
      ref="intercom-address-autocomplete-menu"
      v-model="showModal"
      max-width="350px"
      no-focus
      no-parent-event
      tabindex="1"
      transition-duration="0"
    >
      <template v-if="hasResults">
        <template v-if="existingReports.length > 0">
          <div id="intercom-address-autocomplete-existing-reports" class="q-pa-sm q-gutter-y-xs">
            <button
              v-for="(report, idx) in existingReports"
              :id="`option-${idx}`"
              :key="report.id"
              class="existing-report q-pa-sm no-button full-width flex justify-between items-center"
              @click="clickedReport(report.id)"
              @keydown.down.prevent="focusNext(idx + 1)"
              @keydown.up.prevent="
                focusNext(idx === 0 ? existingReports.length + options.length - 1 : idx - 1)
              "
            >
              <div style="font-size: 13px">
                <div class="over-title" style="font-size: 10px">Existing Report</div>
                <div class="name">
                  {{ report.name }}
                </div>
                <div class="tag">
                  Last Modified:
                  {{ formatDate(report.modified_on) }}
                </div>
              </div>
              <div>
                <q-avatar v-if="report.user" round size="30px">
                  <q-img
                    v-if="report.user.picture"
                    :src="report.user.picture"
                    no-spinner
                    referrerpolicy="no-referrer"
                  />
                  <q-icon v-else name="person" />
                  <q-tooltip>
                    <template v-if="report.user.given_name && report.user.family_name">
                      Owner: {{ report.user.given_name }} {{ report.user.family_name }}
                    </template>
                    <template v-else> Owner: {{ report.user.email }} </template>
                  </q-tooltip>
                </q-avatar>
                <q-skeleton
                  v-else
                  animation="blink"
                  style="width: 30px; height: 30px"
                  type="circle"
                />
              </div>
            </button>
          </div>
          <q-separator />
        </template>
        <div
          v-if="options.length > 0 && !showSurfacedReportsOnly"
          id="intercom-address-autocomplete-results"
          class="q-pa-sm q-gutter-y-xs"
        >
          <button
            v-for="(option, idx) in options"
            :id="`option-${existingReports.length + idx}`"
            :key="option.id"
            class="option-section q-pa-sm no-button full-width"
            @click="rowClicked(option)"
            @keydown.down.prevent="
              focusNext(
                existingReports.length + idx === existingReports.length + options.length - 1
                  ? 0
                  : idx + existingReports.length + 1
              )
            "
            @keydown.up.prevent="
              focusNext(
                idx + existingReports.length === 0
                  ? existingReports.length + options.length - 1
                  : idx + existingReports.length - 1
              )
            "
          >
            <div class="building-name">
              {{ option.building_name }}
            </div>
            <div class="address">
              {{ option.street_address }}, {{ option.city }}, {{ option.state }}
              {{ option.zip_code }}
            </div>
            <div>
              <div v-if="option.year_built" class="property-tag">
                Year Built: {{ option.year_built }}
              </div>
              <div v-if="option.number_units" class="property-tag">
                Number Units: {{ option.number_units }}
              </div>
            </div>
          </button>
        </div>
      </template>
      <div v-else class="q-pa-sm">No results found</div>
    </q-menu>
  </div>
</template>

<script lang="ts">
import type {
  AccessUser,
  PropertySearchResponse,
  PropertyReportSubscriptionAttributes
} from 'common'
import { usePropertyStore } from '@/stores/property'
import type { QMenu } from 'quasar'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { formatDate } from '@/utils'
import { useUserStore } from '@/stores/user'
import { levenshteinDistance } from 'common/utils'

export default {
  name: 'AddressAutocomplete',
  components: {},
  props: {
    placeholder: {
      type: String,
      default: 'Enter an address or building name'
    },
    dense: {
      type: Boolean,
      default: false
    },
    lat: {
      type: Number,
      default: null
    },
    lon: {
      type: Number,
      default: null
    },
    showIcon: {
      type: Boolean,
      default: false
    },
    surfaceReports: {
      type: Boolean,
      default: false
    },
    showSurfacedReportsOnly: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    disable: {
      type: Boolean,
      default: false
    },
    initialValue: {
      type: String,
      default: undefined
    }
  },
  emits: ['new-address'],
  data() {
    return {
      options: [] as PropertySearchResponse[],
      loading: false,
      searchText: null as string | null,
      openMenu: false,
      searchHistory: [] as string[] // a stack of the latest N searches.
    }
  },
  computed: {
    allReports() {
      return usePropertyReportStore().existingReports
    },
    existingReports(): PropertyReportSubscriptionAttributes[] {
      if (!this.surfaceReports) {
        return []
      }
      const relevantReports = this.allReports.filter(
        (r) =>
          this.options.length > 0 &&
          r.subject?.id &&
          this.options.some((_) => _.id === r.subject?.id)
      )

      // sort by modified_on (YYYY-MM-DD) descending
      return (
        relevantReports
          .sort((a, b) => b.modified_on.localeCompare(a.modified_on))
          // return top 5
          .slice(0, 5)
          .map((r) => {
            return {
              ...r,
              user: this.getUser(r.user_id)
            }
          })
      )
    },
    showModal: {
      get() {
        if (this.options.length <= 0) {
          return false
        }
        if (this.searchText === null || this.searchText.length < 3) {
          return false
        }
        return this.openMenu
      },
      set(val: boolean) {
        this.openMenu = val
      }
    },
    hasResults() {
      if (this.showSurfacedReportsOnly) {
        return this.existingReports.length > 0
      }
      return this.existingReports.length > 0 || this.options.length > 0
    }
  },
  watch: {
    searchText(val) {
      if (val === null || val.length < 3) {
        this.options = []
      }
      this.search(val)
    },
    searchHistory() {
      const failedSearch = this.isFailedSearch()
      if (failedSearch) {
        this.$tracker.failedSearch(failedSearch)
      }
    }
  },
  methods: {
    formatDate,
    focusNext(idx: number) {
      document.getElementById(`option-${idx}`)?.focus()
    },
    clickedReport(reportId: number) {
      this.searchHistory = []
      this.$router.push({ name: 'Property Report', params: { reportId } })
    },
    getUser(id: string): AccessUser | undefined {
      return useUserStore().peopleInOrgOrSharedPerId.get(id)
    },
    search(val: string | number | null) {
      if (typeof val === 'number') {
        return
      } else if (val && val.length > 3) {
        this.loading = true
        usePropertyStore()
          .propertySearch(val, { lat: this.lat ?? undefined, lon: this.lon ?? undefined }, true)
          .then((options) => {
            this.options = options

            // append to the history
            this.addToHistory(val)

            // on next tick, we get the id intercom-address-autocomplete-menu and call updatePosition() to reposition the menu
            this.$nextTick(() => {
              const menu = this.$refs['intercom-address-autocomplete-menu'] as QMenu | undefined
              if (menu) {
                menu.updatePosition()
              }
            })
          })
          .catch((e) => {
            if (e.name === 'CancelError') {
              return
            }
          })
          .finally(() => {
            this.loading = false
          })
      } else if (val === null) {
        this.options = []
        this.addToHistory(val)
      } else {
        this.addToHistory(null)
      }
    },
    addToHistory(val: string | null) {
      if (val === this.searchHistory[this.searchHistory.length - 1]) {
        return
      }
      this.searchHistory.push(val || '')
      this.searchHistory = this.searchHistory.slice(-20)
    },
    rowClicked(row: PropertySearchResponse) {
      this.$emit('new-address', row)

      if (this.searchText) {
        const selectedIdx = this.options.findIndex((_) => _.id === row.id)
        this.$tracker.selectedSearchedAddress(this.searchText, selectedIdx)
      }

      this.searchHistory = []
      this.options = []
      this.searchText = null
      this.loading = false
    },
    isFailedSearch(): string | false {
      // use searchHistory to detect if the last search was a failed search. Here are cases that would qualify:
      // 1. the last search is empty but the previous search was 5+ characters. For example, if the user searches "123 Main St" and then clears the input, we should not consider "123 Main St" as a failed search.
      // 2. the last search is very different from the previous search (e.g. "123 Main St" vs "The Monitor, 555 Second St, San Francisco, CA 94101"). In that case, 123 Main St is a failed search.
      // For "different", we can use the levenshtein distance.

      const histLength = this.searchHistory.length
      if (histLength < 2) {
        return false
      }

      const [prev, last] = this.searchHistory.slice(-2)

      // if they are not strings, then we're not searching for an address
      if (typeof prev !== 'string' || typeof last !== 'string') {
        return false
      }

      // we need at least 5 characters to be a valid search
      if (prev.length < 5) {
        return false
      }

      // if the previous search was 5+ characters and the last search was empty, then some intense deletion happened
      if (prev.length > 5 && last.length < 1) {
        return prev
      }

      // if prev is included in last, then we're still searching for the same address
      if (last.includes(prev)) {
        return false
      }

      const distance = levenshteinDistance(prev, last)
      if (distance && distance > 5) {
        return prev
      }
      return false
    }
  },
  mounted() {
    if (this.initialValue) {
      this.searchText = this.initialValue
    }
  }
}
</script>
<style lang="scss" scoped>
@use 'sass:color';

.option-section {
  border-radius: 4px;

  .building-name {
    font-size: 14px;
    font-weight: 500;
  }

  .address {
    font-size: 12px;
  }
}

.body--dark {
  .option-section {
    .address {
      color: white;
    }

    .property-tag {
      color: white;
    }

    &:hover {
      background: linear-gradient($primary, color.scale($primary, $lightness: -5%));
      color: white;

      .address {
        color: white;
      }

      .property-tag {
        border: 1px solid $grey-3;
        background-color: $grey-2;
        color: $grey-9;
      }
    }
  }

  .existing-report {
    .over-title {
      color: $grey-6;
    }

    .tag {
      color: $grey-5;
    }

    &:hover {
      background-color: $grey-1;

      .over-title {
        color: $primary;
      }

      .name {
        font-weight: 500;
        color: $primary;
      }

      .tag {
        color: $primary;
      }
    }
  }
}

.body--light {
  .option-section {
    .address {
      color: $grey-9;
    }

    &:hover {
      color: $primary;
      background-color: $grey-1;

      .address {
        color: $primary;
      }

      .property-tag {
        border: 1px solid $primary;
        color: $primary;
      }
    }
  }
  .existing-report {
    font-size: 13px;

    .over-title {
      color: $grey-6;
    }

    .tag {
      color: $grey-7;
      font-size: 12px;
    }

    &:hover {
      background-color: $grey-1;

      .over-title {
        color: $primary;
      }

      .name {
        font-weight: 500;
        color: $primary;
      }

      .tag {
        color: $primary;
      }
    }
  }
}

.property-tag {
  display: inline-block;
  margin-right: 5px;
  padding: 2px 5px;
  border-radius: 4px;
  border: 1px solid $grey-5;
  color: $grey-9;
  font-size: small;
  margin-top: 2px;
}

.no-button {
  background: none;
  color: inherit;
  border: none;
  padding: inherit;
  font: inherit;
  cursor: pointer;
  text-align: left;
}
</style>
